export const GENDER_COLOR_MAP = {
    masculine: {
        theme: 'blue',
        hex: '#2563eb', // Cobalt Blue
        name: 'Sapphire Blue',
        badge: '🔵 der',
    },
    feminine: {
        theme: 'red',
        hex: '#dc2626', // Crimson Red
        name: 'Ruby Red',
        badge: '🔴 die',
    },
    neuter: {
        theme: 'green',
        hex: '#16a34a', // Emerald Green
        name: 'Emerald Green',
        badge: '🟢 das',
    },
    neutral: {
        theme: 'neutral',
        hex: '#6b7280', // Slate Gray
        name: 'Neutral Slate',
        badge: '⚪',
    },
};
/**
 * Builds chromatic visual metadata and color-constrained AI diffusion prompts
 */
export function buildVisualMnemonic(lemma, translation, gender = 'neutral') {
    const colorInfo = GENDER_COLOR_MAP[gender] || GENDER_COLOR_MAP.neutral;
    let colorDescription;
    switch (colorInfo.theme) {
        case 'blue':
            colorDescription =
                'cinematic studio lighting with dominant cool blue ambient rim light, sapphire and cobalt accents, clean high-contrast composition';
            break;
        case 'red':
            colorDescription =
                'warm ruby and rose lighting, vibrant crimson environment accents, clean high-contrast composition';
            break;
        case 'green':
            colorDescription =
                'natural emerald and mint lighting, verdant organic backdrop, green accent tones, clean high-contrast composition';
            break;
        default:
            colorDescription =
                'balanced naturalistic daylight, clean minimalist composition, neutral modern styling';
            break;
    }
    const prompt = `A clear, high quality iconic illustration of ${translation} (${lemma}), ${colorDescription}, 4k, digital art, sharp focus, vibrant, clean background, no text, no typography, no letters.`;
    return {
        theme_color: colorInfo.theme,
        hex: colorInfo.hex,
        image_prompt: prompt,
    };
}
const COLOR_WORD = {
    masculine: 'blue',
    feminine: 'red',
    neuter: 'green',
    neutral: null,
};
/**
 * Builds an image prompt where the subject itself and the whole scene carry the gender colour
 * (a feminine "cat" becomes a red cat in a red scene). Words without gender get natural colours.
 * The colour clause is always added here so an AI-written subject can never omit it.
 */
export function buildSubjectImagePrompt(subject, gender = 'neutral') {
    const colorInfo = GENDER_COLOR_MAP[gender] || GENDER_COLOR_MAP.neutral;
    const colorWord = COLOR_WORD[gender] ?? null;
    const cleanSubject = subject
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[.!]+$/, '');
    const prompt = colorWord
        ? `${cleanSubject.replace(/^(an?|the)\s+/i, (m) => `${m.trim().replace(/^(a)n$/i, '$1')} ${colorWord} `)}. Everything in the image is ${colorWord}: the subject, the background and the lighting are all ${colorWord} tones. Clean digital illustration, sharp focus, no text, no letters.`
        : `${cleanSubject}. Natural, balanced colors. Clean digital illustration, sharp focus, no text, no letters.`;
    return {
        theme_color: colorInfo.theme,
        hex: colorInfo.hex,
        image_prompt: prompt,
    };
}
/** Card artwork size. Small on purpose: images are billed per pixel and shown at about 400x260. */
export const CARD_IMAGE_WIDTH = 512;
export const CARD_IMAGE_HEIGHT = 352;
/** Only nouns and verbs get a picture; phrases, adjectives and the rest are text-only cards. */
export function wantsCardImage(partOfSpeech) {
    return partOfSpeech === 'noun' || partOfSpeech === 'verb';
}
/** Picture description for a word that has no curated subject (built from its English translation). */
export function imageSubjectFor(partOfSpeech, translation) {
    const meaning = translation.replace(/\s+/g, ' ').trim().slice(0, 100);
    if (partOfSpeech === 'verb')
        return `a person in the middle of the action "${meaning}"`;
    return /^(an?|the)\s/i.test(meaning) ? meaning : `a ${meaning}`;
}
/**
 * Returns CSS styles for dynamic chromatic overlay and gradient borders
 */
export function getChromaticStyles(theme, hex) {
    return {
        borderColor: hex,
        badgeBg: hex,
        glowStyle: `0 0 16px ${hex}33`,
        gradientOverlay: `linear-gradient(180deg, transparent 60%, ${hex}22 100%)`,
        duotoneFilter: `drop-shadow(0 4px 12px ${hex}40)`,
    };
}
